import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import Divider from '@mui/material/Divider';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { apiFetch } from '../api/api';
import { SERVICE_ID } from '../config';
import { isUserLoggedIn, readJsonSafe } from '../utils/authSession';
import { extractOrders, mapOrder } from '../utils/mapOrder';
import {
  formatOrderDate,
  formatOrderTotal,
  getOrderStatusColors,
  hasOrderMoney,
  translateDeliveryType,
  translateOrderStatus,
} from '../utils/orderDisplay';

const pagePaperSx = {
  elevation: 0,
  variant: 'outlined',
  sx: {
    borderRadius: 4,
    bgcolor: 'background.paper',
    boxShadow: '0 4px 24px rgba(184,137,74,0.06)',
    borderColor: 'divider',
    overflow: 'hidden',
  },
};

const primaryButtonSx = {
  py: 1.5,
  borderRadius: 2.5,
  fontSize: '1rem',
  fontWeight: 800,
  background: 'linear-gradient(90deg, #D4A574, #B8894A)',
  boxShadow: '0 6px 18px rgba(184,137,74,0.28)',
  '&:hover': {
    background: 'linear-gradient(90deg, #B8894A, #8F6A35)',
  },
};

function PageTitle({ title, subtitle }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.25, md: 1.5 }, flex: 1, minWidth: 0 }}>
      <Box
        sx={{
          width: 5,
          height: { xs: 28, md: 34 },
          borderRadius: 999,
          flexShrink: 0,
          background: 'linear-gradient(180deg, #D4A574, #B8894A)',
          boxShadow: '0 2px 10px rgba(184,137,74,0.28)',
        }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="h4"
          fontWeight={800}
          sx={{
            color: 'primary.main',
            fontSize: { xs: '1.45rem', md: '1.75rem' },
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
        {subtitle ? (
          <Typography color="text.secondary" sx={{ mt: 0.5, fontSize: '0.95rem', fontWeight: 600 }}>
            {subtitle}
          </Typography>
        ) : null}
        <Box
          sx={{
            mt: 0.75,
            width: { xs: 40, md: 52 },
            height: 3,
            borderRadius: 999,
            background: 'linear-gradient(90deg, #D4A574, #B8894A)',
            opacity: 0.85,
          }}
        />
      </Box>
    </Box>
  );
}

function MetaItem({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: 2.5,
          bgcolor: alpha('#B8894A', 0.08),
          border: 1,
          borderColor: alpha('#B8894A', 0.14),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 18, color: 'primary.main' }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={700} lineHeight={1.5}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

function OrderCard({ order, locale, isDarkMode }) {
  const { t } = useTranslation();
  const statusColors = getOrderStatusColors(order.status, isDarkMode);
  const statusLabel = translateOrderStatus(order.status, t);
  const orderLabel = order.number || `#${order.id}`;
  const dateValue = order.createdAt || order.humanDate;
  const deliveryFeeNum = Number(order.deliveryPrice);
  const isFreeDelivery = Number.isFinite(deliveryFeeNum) && deliveryFeeNum <= 0;
  const discountAmountNum = Number(order.discountAmount);

  return (
    <Paper
      {...pagePaperSx}
      sx={{
        ...pagePaperSx.sx,
        transition: 'all 0.22s ease',
        '&:hover': {
          borderColor: 'primary.light',
          boxShadow: '0 8px 28px rgba(184,137,74,0.14)',
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          background: 'linear-gradient(180deg, rgba(212,165,116,0.1) 0%, rgba(184,137,74,0.04) 100%)',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2.5,
              bgcolor: 'background.paper',
              border: 1,
              borderColor: alpha('#B8894A', 0.16),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 4px 12px rgba(184,137,74,0.12)',
            }}
          >
            <ReceiptLongOutlinedIcon color="primary" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              {t('orders_number')}
            </Typography>
            <Typography variant="h6" fontWeight={800} noWrap color="primary.main">
              {orderLabel}
            </Typography>
          </Box>
        </Box>
        <Chip
          label={statusLabel}
          size="small"
          sx={{
            fontWeight: 800,
            bgcolor: statusColors.bgcolor,
            color: statusColors.color,
            borderRadius: 2,
            maxWidth: 140,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}
        />
      </Box>

      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Grid container spacing={2.5} sx={{ mb: order.items?.length || order.note ? 2.5 : 0 }}>
          {dateValue && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <MetaItem
                icon={CalendarTodayOutlinedIcon}
                label={t('orders_date')}
                value={formatOrderDate(dateValue, locale)}
              />
            </Grid>
          )}
          {order.cityName && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <MetaItem icon={LocationOnOutlinedIcon} label={t('orders_city')} value={order.cityName} />
            </Grid>
          )}
          {order.location && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <MetaItem icon={LocationOnOutlinedIcon} label={t('orders_location')} value={order.location} />
            </Grid>
          )}
          {order.deliveryType && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <MetaItem
                icon={LocalShippingOutlinedIcon}
                label={t('orders_delivery')}
                value={translateDeliveryType(order.deliveryType, t)}
              />
            </Grid>
          )}
          {hasOrderMoney(order.deliveryPrice) && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <MetaItem
                icon={LocalShippingOutlinedIcon}
                label={t('orders_delivery_fee')}
                value={
                  isFreeDelivery
                    ? t('orders_delivery_free')
                    : `${formatOrderTotal(order.deliveryPrice)} ${t('orders_currency')}`
                }
              />
            </Grid>
          )}
          {hasOrderMoney(order.discountAmount) && discountAmountNum > 0 && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <MetaItem
                icon={Inventory2OutlinedIcon}
                label={t('orders_discount')}
                value={`-${formatOrderTotal(order.discountAmount)} ${t('orders_currency')}`}
              />
            </Grid>
          )}
        </Grid>

        {order.items?.length > 0 ? (
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              mb: order.note ? 2 : 0,
              borderColor: alpha('#B8894A', 0.12),
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.25,
                bgcolor: alpha('#B8894A', 0.06),
                borderBottom: 1,
                borderColor: alpha('#B8894A', 0.1),
              }}
            >
              <Typography variant="caption" fontWeight={800} color="primary.main">
                {t('products')}
              </Typography>
            </Box>
            <Stack divider={<Divider sx={{ borderColor: alpha('#B8894A', 0.08) }} />}>
              {order.items.map((item, index) => (
                <Box key={`${order.id}-item-${item.id ?? index}`} sx={{ px: 2, py: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
                    <Typography variant="body2" fontWeight={700} sx={{ flex: 1 }}>
                      {item.name}
                      {item.quantity > 1 ? ` ×${item.quantity}` : ''}
                    </Typography>
                    {hasOrderMoney(item.price) && (
                      <Typography variant="body2" fontWeight={800} color="primary.main" sx={{ flexShrink: 0 }}>
                        {formatOrderTotal(item.price)} {t('orders_currency')}
                      </Typography>
                    )}
                  </Box>
                  {item.description && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {item.description}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Paper>
        ) : order.itemsSummary ? (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 3,
              mb: order.note ? 2 : 0,
              bgcolor: alpha('#B8894A', 0.04),
              borderColor: alpha('#B8894A', 0.12),
            }}
          >
            <Typography variant="caption" fontWeight={800} color="primary.main" display="block" gutterBottom>
              {t('products')}
            </Typography>
            <Typography variant="body2" fontWeight={600}>{order.itemsSummary}</Typography>
          </Paper>
        ) : order.itemCount > 0 ? (
          <MetaItem
            icon={Inventory2OutlinedIcon}
            label={t('products')}
            value={t('orders_items_count', { count: order.itemCount })}
          />
        ) : null}

        {order.note && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: alpha('#B8894A', 0.04),
              borderColor: alpha('#B8894A', 0.12),
              mt: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <StickyNote2OutlinedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              <Typography variant="caption" fontWeight={800} color="primary.main">
                {t('order_note')}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              {order.note}
            </Typography>
          </Paper>
        )}

        {hasOrderMoney(order.total) && (
          <Box
            sx={{
              mt: 2.5,
              p: 2,
              borderRadius: 3,
              bgcolor: alpha('#B8894A', 0.07),
              border: 1,
              borderColor: alpha('#B8894A', 0.14),
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="subtitle2" fontWeight={800} color="primary.main">
              {t('orders_total')}
            </Typography>
            <Typography variant="h5" fontWeight={800} color="primary.main">
              {formatOrderTotal(order.total)} {t('orders_currency')}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

export default function Orders() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const muiTheme = useMuiTheme();
  const isDarkMode = muiTheme.palette.mode === 'dark';

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [loggedIn, setLoggedIn] = useState(null);

  const loadOrders = useCallback(async (isRefresh = false) => {
    const isLogged = await isUserLoggedIn();
    setLoggedIn(isLogged);
    if (!isLogged) {
      setOrders([]);
      setLoading(false);
      setRefreshing(false);
      return;
    }

    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError('');

    try {
      const response = await apiFetch('/users/orders');
      const json = await readJsonSafe(response);

      if (!response.ok) {
        setError(json.message ?? t('orders_load_failed'));
        setOrders([]);
        return;
      }

      setOrders(
        extractOrders(json)
          .map((order) => mapOrder(order, i18n.language))
          .filter((order) => {
            if (!order.id) return false;
            if (order.serviceId == null) return true;
            return Number(order.serviceId) === SERVICE_ID;
          }),
      );
    } catch {
      setError(t('orders_load_failed'));
      setOrders([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [i18n.language, t]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    if (loggedIn === false) {
      navigate('/signin', { replace: true });
    }
  }, [loggedIn, navigate]);

  const showLoading = loading && !refreshing;
  const orderCountLabel = showLoading
    ? '...'
    : orders.length > 0
      ? `${orders.length} ${t('products').toLowerCase()}`
      : t('orders_empty');

  if (loggedIn === false) {
    return null;
  }

  return (
    <Box sx={{ bgcolor: 'background.default', pb: { xs: 4, md: 6 }, minHeight: '60vh' }}>
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          py: { xs: 2.5, md: 3 },
          mb: { xs: 3, md: 4 },
        }}
      >
        <Container maxWidth="lg">
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            sx={{
              mb: 1.5,
              '& .MuiBreadcrumbs-li': { fontSize: '0.9rem' },
              '& .MuiLink-root:hover': { color: 'primary.main' },
            }}
          >
            <Link component={RouterLink} to="/" underline="hover" color="inherit">
              {t('app_name')}
            </Link>
            <Typography color="text.primary" fontWeight={700}>
              {t('orders.header')}
            </Typography>
          </Breadcrumbs>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <PageTitle title={t('orders.header')} subtitle={orderCountLabel} />
            <IconButton
              onClick={() => loadOrders(true)}
              disabled={refreshing || showLoading}
              aria-label={t('orders_retry')}
              sx={{
                flexShrink: 0,
                width: 44,
                height: 44,
                border: 1,
                borderColor: 'divider',
                borderRadius: 2.5,
                bgcolor: 'background.paper',
                boxShadow: '0 2px 8px rgba(184,137,74,0.1)',
                color: 'primary.main',
                '&:hover': { bgcolor: alpha('#B8894A', 0.06) },
              }}
            >
              <RefreshIcon
                sx={{
                  animation: refreshing ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } },
                }}
              />
            </IconButton>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {showLoading ? (
          <Stack spacing={2.5}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={240} sx={{ borderRadius: 4 }} />
            ))}
          </Stack>
        ) : error ? (
          <Paper
            {...pagePaperSx}
            sx={{ ...pagePaperSx.sx, p: { xs: 4, md: 6 }, textAlign: 'center', maxWidth: 480, mx: 'auto' }}
          >
            <Box
              sx={{
                width: 88,
                height: 88,
                borderRadius: '50%',
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha('#d32f2f', 0.08),
                border: 1,
                borderColor: alpha('#d32f2f', 0.16),
              }}
            >
              <ErrorOutlineOutlinedIcon sx={{ fontSize: 44, color: 'error.main' }} />
            </Box>
            <Typography color="text.secondary" fontWeight={600} sx={{ mb: 3 }}>
              {error}
            </Typography>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={() => loadOrders()}
              sx={{ ...primaryButtonSx, py: 1.25, px: 3 }}
            >
              {t('orders_retry')}
            </Button>
          </Paper>
        ) : orders.length === 0 ? (
          <Paper
            {...pagePaperSx}
            sx={{ ...pagePaperSx.sx, p: { xs: 4, md: 8 }, textAlign: 'center', maxWidth: 480, mx: 'auto' }}
          >
            <Box
              sx={{
                width: 96,
                height: 96,
                borderRadius: '50%',
                mx: 'auto',
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha('#B8894A', 0.08),
                border: 1,
                borderColor: alpha('#B8894A', 0.14),
                boxShadow: '0 8px 24px rgba(184,137,74,0.12)',
              }}
            >
              <ReceiptLongOutlinedIcon sx={{ fontSize: 48, color: 'primary.main' }} />
            </Box>
            <Typography variant="h6" fontWeight={800} color="primary.main" gutterBottom>
              {t('orders_empty')}
            </Typography>
            <Button
              component={RouterLink}
              to="/"
              variant="contained"
              size="large"
              sx={{ mt: 2, px: 4, ...primaryButtonSx }}
            >
              {t('categories')}
            </Button>
          </Paper>
        ) : (
          <Stack spacing={2.5}>
            {orders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                locale={i18n.language}
                isDarkMode={isDarkMode}
              />
            ))}
          </Stack>
        )}
      </Container>
    </Box>
  );
}
